import { supabase } from "@/shared/lib/supabase";
import type { MatrixColumnType } from "./types";

type FunctionErrorWithContext = {
  context?: unknown;
};

/**
 * The Edge Function's error body carries the message the user gets to see, but
 * supabase-js hands it over as a Response on the error. Unwrap it so callers
 * see the payload rather than "Edge Function returned a non-2xx status code".
 */
async function throwFunctionInvokeError(error: unknown): Promise<never> {
  const context =
    error && typeof error === "object" && "context" in error
      ? (error as FunctionErrorWithContext).context
      : undefined;

  if (context instanceof Response) {
    const payload: unknown = await context
      .clone()
      .json()
      .catch(() => error);
    throw payload;
  }

  throw context ?? error;
}

/**
 * One call to the matrix Edge Function. Responses are small acknowledgements,
 * so the caller picks the fields it needs off the returned record; the
 * `require*` helpers below turn a missing one into a named error rather than an
 * `undefined` that only surfaces further down.
 */
async function invokeMatrix(
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body?: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const response = await supabase.functions.invoke(path, {
    method,
    ...(body ? { body } : {}),
  });

  if (response.error) await throwFunctionInvokeError(response.error);

  const data: unknown = response.data;
  return data && typeof data === "object"
    ? (data as Record<string, unknown>)
    : {};
}

function requireString(
  payload: Record<string, unknown>,
  key: string,
  route: string,
): string {
  const value = payload[key];
  if (typeof value !== "string") {
    throw new Error(`Missing ${key} from ${route}`);
  }
  return value;
}

function requireNumber(
  payload: Record<string, unknown>,
  key: string,
  route: string,
): number {
  const value = payload[key];
  if (typeof value !== "number") {
    throw new Error(`Missing ${key} from ${route}`);
  }
  return value;
}

function requireDeleted(
  payload: Record<string, unknown>,
  route: string,
): { deleted: true } {
  if (payload.deleted !== true) {
    throw new Error(`Missing deleted flag from ${route}`);
  }
  return { deleted: true };
}

export type CreateMatrixInput = {
  name: string;
  question?: string;
  templateId?: string;
};

export async function createMatrix(
  input: CreateMatrixInput,
): Promise<{ tenant_collection_id: string }> {
  const payload = await invokeMatrix("matrix", "POST", {
    name: input.name.trim(),
    ...(input.question !== undefined ? { question: input.question } : {}),
    ...(input.templateId ? { template_id: input.templateId } : {}),
  });
  return {
    tenant_collection_id: requireString(
      payload,
      "tenant_collection_id",
      "create matrix",
    ),
  };
}

export type UpdateMatrixInput = {
  name?: string;
  question?: string;
};

export async function updateMatrix(
  collectionId: string,
  input: UpdateMatrixInput,
): Promise<{ tenant_collection_id: string }> {
  const payload = await invokeMatrix(`matrix/${collectionId}`, "PATCH", input);
  return {
    tenant_collection_id: requireString(
      payload,
      "tenant_collection_id",
      "update matrix",
    ),
  };
}

/**
 * Copies the matrix and everything in it into a new one of your own. Nothing
 * runs, so it costs no credits; `name` is optional and the server falls back to
 * "<name> (copy)".
 */
export async function duplicateMatrix(
  collectionId: string,
  name?: string,
): Promise<{ tenant_collection_id: string }> {
  const payload = await invokeMatrix(
    `matrix/${collectionId}/duplicate`,
    "POST",
    name ? { name: name.trim() } : {},
  );
  return {
    tenant_collection_id: requireString(
      payload,
      "tenant_collection_id",
      "duplicate matrix",
    ),
  };
}

export async function deleteMatrix(
  collectionId: string,
): Promise<{ deleted: true }> {
  const payload = await invokeMatrix(`matrix/${collectionId}`, "DELETE");
  return requireDeleted(payload, "delete matrix");
}

export type AddMatrixRowInput = {
  inputResults?: Record<string, string>;
};

export async function addMatrixRow(
  collectionId: string,
  input: AddMatrixRowInput = {},
): Promise<{ analysis_id: string }> {
  const payload = await invokeMatrix(`matrix/${collectionId}/rows`, "POST", {
    input_results: input.inputResults ?? {},
  });
  return {
    analysis_id: requireString(payload, "analysis_id", "add matrix row"),
  };
}

export async function deleteMatrixRow(
  collectionId: string,
  analysisId: string,
): Promise<{ deleted: true }> {
  const payload = await invokeMatrix(
    `matrix/${collectionId}/rows/${analysisId}`,
    "DELETE",
  );
  return requireDeleted(payload, "delete matrix row");
}

export type AddMatrixColumnInput = {
  label: string;
  type?: MatrixColumnType;
  displayDescription?: string;
  width?: number;
};

export async function addMatrixColumn(
  collectionId: string,
  input: AddMatrixColumnInput,
): Promise<{ key: string }> {
  const payload = await invokeMatrix(`matrix/${collectionId}/columns`, "POST", {
    label: input.label,
    ...(input.type === undefined ? {} : { type: input.type }),
    ...(input.width === undefined ? {} : { width: input.width }),
    ...(input.displayDescription === undefined
      ? {}
      : { display_description: input.displayDescription }),
  });
  return { key: requireString(payload, "key", "add matrix column") };
}

export type UpdateMatrixColumnInput = {
  label?: string;
  displayDescription?: string;
  type?: MatrixColumnType;
  width?: number;
  order?: number;
};

export async function updateMatrixColumn(
  collectionId: string,
  columnKey: string,
  input: UpdateMatrixColumnInput,
): Promise<{ key: string }> {
  const payload = await invokeMatrix(
    `matrix/${collectionId}/columns/${columnKey}`,
    "PATCH",
    {
      ...(input.label === undefined ? {} : { label: input.label }),
      ...(input.type === undefined ? {} : { type: input.type }),
      ...(input.width === undefined ? {} : { width: input.width }),
      ...(input.order === undefined ? {} : { order: input.order }),
      ...(input.displayDescription === undefined
        ? {}
        : { display_description: input.displayDescription }),
    },
  );
  return { key: requireString(payload, "key", "update matrix column") };
}

export async function deleteMatrixColumn(
  collectionId: string,
  columnKey: string,
): Promise<{ deleted: true }> {
  const payload = await invokeMatrix(
    `matrix/${collectionId}/columns/${columnKey}`,
    "DELETE",
  );
  return requireDeleted(payload, "delete matrix column");
}

export type UpdateMatrixCellInput = {
  analysisId: string;
  columnKey: string;
  value: string;
};

export async function updateMatrixCell(
  collectionId: string,
  input: UpdateMatrixCellInput,
): Promise<{ analysis_id: string; column_key: string }> {
  const payload = await invokeMatrix(`matrix/${collectionId}/cells`, "PATCH", {
    analysis_id: input.analysisId,
    column_key: input.columnKey,
    value: input.value,
  });
  return {
    analysis_id: requireString(payload, "analysis_id", "update matrix cell"),
    column_key: requireString(payload, "column_key", "update matrix cell"),
  };
}

export type RunMatrixInput = {
  analysisIds?: string[];
};

export async function runMatrix(
  collectionId: string,
  input: RunMatrixInput = {},
): Promise<{ analyses_queued: number }> {
  const payload = await invokeMatrix(`matrix/${collectionId}/run`, "POST", {
    ...(input.analysisIds ? { analysis_ids: input.analysisIds } : {}),
  });
  return {
    analyses_queued: requireNumber(payload, "analyses_queued", "run matrix"),
  };
}
